import React, { useState } from 'react';
import { X, CheckCircle, ArrowLeft, ArrowRight, Wallet, Check, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const ValidateSalaryModal = ({ open, onOpenChange }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [validationType, setValidationType] = useState('pay'); // 'validate' or 'pay'
  const [adjustment, setAdjustment] = useState('0,00');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  
  if (!open) return null;

  const stepperLabels = [
    { step: 1, label: 'Présences', sub: 'Vérifiez et ajustez les journées' },
    { step: 2, label: 'Calcul du salaire', sub: 'Détail du calcul' },
    { step: 3, label: 'Net à payer', sub: 'Vérification finale' },
    { step: 4, label: 'Validation & paiement', sub: 'Finalisez le traitement' }
  ];

  const handleNext = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = () => {
    onOpenChange(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-900 leading-tight">Valider le salaire du mois – Août 2026</h2>
          <button onClick={() => onOpenChange(false)} className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Content containing steps layout */}
        <div className="flex p-6 gap-6 overflow-x-auto">
          
          {/* STEP 1 */}
          <div className={`flex-shrink-0 w-80 rounded-xl border-2 p-5 ${currentStep >= 1 ? 'border-violet-600 shadow-sm bg-white' : 'border-gray-200 bg-gray-50 opacity-50'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${currentStep >= 1 ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-500'}`}>1</div>
              <div>
                <h3 className="font-bold text-gray-900">Présences</h3>
                <p className="text-xs text-gray-500">Vérifiez et ajustez les journées</p>
              </div>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-gray-600">Jours prévus</span>
                <span className="font-medium">26</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-gray-600">Jours travaillés</span>
                <span className="font-medium text-emerald-600">22</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-gray-600">Congés payés</span>
                <span className="font-medium text-orange-500">1</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-gray-600">Repos</span>
                <span className="font-medium">1</span>
              </div>
              <div className="flex justify-between py-2 border-b border-red-50 bg-red-50/50 -mx-2 px-2 rounded-md">
                <span className="text-red-600 font-medium">Absences</span>
                <span className="font-bold text-red-600">2</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-gray-600">Demi-journées</span>
                <span className="font-medium">0</span>
              </div>
            </div>

            {currentStep === 1 && (
              <Button onClick={handleNext} className="w-full mt-6 bg-violet-600 hover:bg-violet-700">
                Continuer <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
            {currentStep > 1 && (
              <div className="w-full mt-6 py-2 flex justify-center text-emerald-600 bg-emerald-50 rounded-lg">
                <Check className="h-5 w-5" />
              </div>
            )}
          </div>

          {/* STEP 2 */}
          <div className={`flex-shrink-0 w-80 rounded-xl border-2 p-5 ${currentStep === 2 ? 'border-violet-600 shadow-sm bg-white' : currentStep > 2 ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-50'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${currentStep >= 2 ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-500'}`}>2</div>
              <div>
                <h3 className="font-bold text-gray-900">Calcul du salaire</h3>
                <p className="text-xs text-gray-500">Détail du calcul</p>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-1.5">
                <span className="text-gray-500">Salaire mensuel (contrat)</span>
                <span className="font-medium">45 000,00 DA</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-gray-600">Salaire jours travaillés (22 j)</span>
                <span className="font-medium">38 076,94 DA</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-gray-600">Congés payés (1 j)</span>
                <span className="font-medium text-emerald-600">+ 1 730,77 DA</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-gray-600">Prime de rendement</span>
                <span className="font-medium text-emerald-600">+ 5 000,00 DA</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-gray-100 pb-3">
                <span className="text-gray-600">Autres primes / indemnités</span>
                <span className="font-medium text-emerald-600">+ 0,00 DA</span>
              </div>
              
              <div className="flex justify-between py-1.5 pt-3">
                <span className="text-gray-900 font-medium">Sous-total</span>
                <span className="font-bold text-gray-900">44 807,71 DA</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-gray-600">Avance sur salaire</span>
                <span className="font-medium text-red-600">- 10 000,00 DA</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-gray-100 pb-3">
                <span className="text-gray-600">Retenues</span>
                <span className="font-medium text-red-600">- 0,00 DA</span>
              </div>

              <div className="flex justify-between py-3 bg-violet-50 rounded-lg px-3 mt-2">
                <span className="text-violet-900 font-bold">Total à payer</span>
                <span className="font-bold text-violet-700">34 807,71 DA</span>
              </div>
            </div>

            {currentStep === 2 && (
              <Button onClick={handleNext} className="w-full mt-6 bg-violet-600 hover:bg-violet-700">
                Continuer <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
            {currentStep > 2 && (
              <div className="w-full mt-6 py-2 flex justify-center text-emerald-600 bg-emerald-50 rounded-lg">
                <Check className="h-5 w-5" />
              </div>
            )}
          </div>

          {/* STEP 3 */}
          <div className={`flex-shrink-0 w-80 rounded-xl border-2 p-5 ${currentStep === 3 ? 'border-violet-600 shadow-sm bg-white' : currentStep > 3 ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-50'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${currentStep >= 3 ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-500'}`}>3</div>
              <div>
                <h3 className="font-bold text-gray-900">Net à payer</h3>
                <p className="text-xs text-gray-500">Vérification finale</p>
              </div>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-gray-100 mb-4">
              <span className="text-gray-900 font-bold">Total à payer</span>
              <span className="font-bold text-violet-600 text-lg">34 807,71 DA</span>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Ajustement exceptionnel</label>
                <div className="relative">
                  <Input value={adjustment} onChange={(e) => setAdjustment(e.target.value)} className="pr-12 text-right font-medium" disabled={currentStep !== 3} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">DA</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Motif (optionnel)</label>
                <Input value={adjustmentReason} onChange={(e) => setAdjustmentReason(e.target.value)} placeholder="Entrez un motif pour cet ajustement..." disabled={currentStep !== 3} />
              </div>

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-100 text-center mt-6">
                <p className="text-orange-800 text-sm font-bold mb-1">NET À PAYER</p>
                <p className="text-2xl font-bold text-orange-600">34 807,71 DA</p>
              </div>
            </div>

            {currentStep === 3 && (
              <Button onClick={handleNext} className="w-full mt-6 bg-violet-600 hover:bg-violet-700">
                Continuer <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
            {currentStep > 3 && (
              <div className="w-full mt-6 py-2 flex justify-center text-emerald-600 bg-emerald-50 rounded-lg">
                <Check className="h-5 w-5" />
              </div>
            )}
          </div>

          {/* STEP 4 */}
          <div className={`flex-shrink-0 w-80 rounded-xl border-2 p-5 ${currentStep === 4 ? 'border-violet-600 shadow-sm bg-white' : 'border-gray-200 bg-gray-50 opacity-50'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${currentStep >= 4 ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-500'}`}>4</div>
              <div>
                <h3 className="font-bold text-gray-900">Validation & paiement</h3>
                <p className="text-xs text-gray-500">Finalisez le traitement</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${validationType === 'validate' ? 'border-violet-600 bg-violet-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input type="radio" name="valType" className="mt-1 w-4 h-4 text-violet-600 border-gray-300 focus:ring-violet-500" 
                  checked={validationType === 'validate'} onChange={() => setValidationType('validate')} disabled={currentStep !== 4} />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Valider uniquement</p>
                  <p className="text-xs text-gray-500">Enregistre le salaire validé sans effectuer le paiement.</p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${validationType === 'pay' ? 'border-violet-600 bg-violet-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input type="radio" name="valType" className="mt-1 w-4 h-4 text-violet-600 border-gray-300 focus:ring-violet-500" 
                  checked={validationType === 'pay'} onChange={() => setValidationType('pay')} disabled={currentStep !== 4} />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Valider et payer</p>
                  <p className="text-xs text-gray-500">Enregistre le salaire et effectue le paiement.</p>
                </div>
              </label>

              {validationType === 'pay' && (
                <div className="pt-4 border-t border-gray-100 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Mode de paiement</label>
                      <select className="w-full text-sm p-2 rounded border border-gray-200 bg-white" disabled={currentStep !== 4}>
                        <option>Cash (Espèces)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Caisse</label>
                      <select className="w-full text-sm p-2 rounded border border-gray-200 bg-white" disabled={currentStep !== 4}>
                        <option>Caisse principale</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Date de paiement</label>
                      <Input type="date" value="2026-08-31" readOnly className="h-9 text-sm" disabled={currentStep !== 4} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Montant à payer</label>
                      <Input value="34 807,71 DA" readOnly className="h-9 text-sm font-medium bg-gray-50" disabled={currentStep !== 4} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {currentStep === 4 && (
              <div className="flex gap-3 mt-8">
                <Button variant="ghost" onClick={handleBack} className="text-gray-600 hover:bg-gray-100 flex-1">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Retour
                </Button>
                <Button onClick={handleSubmit} className="bg-emerald-500 hover:bg-emerald-600 text-white flex-1">
                  {validationType === 'pay' ? 'Valider et payer' : 'Valider'} <Check className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidateSalaryModal;
